import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowUpRight } from "lucide-react";
import api from "../lib/api";

/**
 * Small ticker on the Dashboard showing the last 5 goal-tasks the user has
 * checked off. Silent when empty (no coaching state — the SampleJourney or
 * Ask-Boardroom already handles empty-state motivation).
 *
 * "You moved the needle today" feedback loop — the current stats page shows
 * *totals*, this shows *momentum*.
 */

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function TaskHistoryFeed() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/tasks/recent-completed?limit=5")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.debug("recent-completed fetch failed", e))
      .finally(() => setLoaded(true));
  }, []);

  // Silent when there's nothing to celebrate yet.
  if (!loaded || items.length === 0) return null;

  return (
    <div className="surface rounded-md p-6" data-testid="task-history-feed">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-eyebrow text-[#9bd3c4]">Momentum</p>
          <h3 className="font-serif text-xl mt-2">You moved the needle</h3>
        </div>
        <Link
          to="/goals"
          className="text-[10px] font-mono uppercase tracking-widest text-[#8a8a8a] hover:text-[#c89f45] transition-colors inline-flex items-center gap-1"
          data-testid="task-history-see-goals"
        >
          Open goals
          <ArrowUpRight size={11} />
        </Link>
      </div>
      <ul className="mt-5 space-y-3" data-testid="task-history-list">
        {items.map((it) => (
          <li
            key={it.task_id}
            className="flex items-start gap-3 border-l border-[#1B3B36] pl-4"
            data-testid={`task-history-item-${it.task_id}`}
          >
            <CheckCircle2 size={14} className="text-[#9bd3c4] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#ddd] truncate" data-testid={`task-history-title-${it.task_id}`}>
                {it.task_title}
              </p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-[#8a8a8a] mt-1">
                <span className="text-[#888]">{it.goal_title}</span>
                <span className="mx-1.5">·</span>
                <span data-testid={`task-history-when-${it.task_id}`}>{timeAgo(it.completed_at)}</span>
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
